import { Component, Input } from '@angular/core';
import { PercentLabel } from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchJobProfile, ResearchRoleMatch } from '../../models/research.models';
import { HeldSkillNames, RoleCardStats } from './career-explorer.state';

export type SectorToneName = 'teal' | 'blue' | 'plum' | 'orange' | 'neutral';
const SECTOR_TONES: readonly SectorToneName[] = ['teal', 'blue', 'plum', 'orange', 'neutral'];

/** P6:345-348: the sector pill's tint. The prototype hand-assigns one per role (`sc`); the wire has only `jobFamily`, so a stable hash picks it. */
export function SectorTone(jobFamily: string): SectorToneName {
    let h = 0;
    for (let i = 0; i < jobFamily.length; i++) h = (h * 31 + jobFamily.charCodeAt(i)) >>> 0;
    return SECTOR_TONES[h % SECTOR_TONES.length];
}

/** How many skill chips a card shows (P6:1615 `skillChips(r, 3)`). */
const MAX_CHIPS = 3;

export interface RoleChip {
    readonly Name: string;
    readonly Held: boolean;
}

export interface RoleCardView {
    readonly Tone: SectorToneName;
    /** 0..100, the ring's fill; produced from the contract's `PercentLabel`, the only place an overlap becomes a percent. */
    readonly Percent: number;
    readonly PercentLabel: string;
    readonly Salary: string | null;
    readonly Education: string | null;
    readonly Chips: readonly RoleChip[];
    readonly More: number;
}

const EMPTY_STATS: { salary: string | null; education: string | null } = { salary: null, education: null };

/**
 * A top-three role card (P6:340-362, 1611-1618): sector pill, title, the match ring, Salary and Education tiles, the first three skills
 * the role asks for (a check on those the CV evidences) and "Explore role →".
 *
 * Deviations from P6, both on purpose:
 * - P6:1600-1638 draws a score RANGE (`sc.lo`..`sc.hi`); the contract returns one 0..1 `overlap`, so the ring shows that single score.
 * - P6:1613 has an Experience tile; the profile carries no experience data, so there is none (never a made-up figure).
 */
@Component({
    standalone: false,
    selector: 'ra-cx-role-card',
    templateUrl: './cx-role-card.component.html',
    styleUrl: './cx-role-card.component.scss',
})
export class CxRoleCardComponent {
    @Input() Match!: ResearchRoleMatch;
    @Input() Profile: ResearchJobProfile | null = null;

    private memo: { match: ResearchRoleMatch; profile: ResearchJobProfile | null; view: RoleCardView } | null = null;

    constructor(private readonly host: ExplorerRaHostAdapter) {}

    /** Recomputed only when the match or profile object changes: the template reads it on every pass (a fresh array each time repaints in a loop). */
    get View(): RoleCardView {
        if (this.memo === null || this.memo.match !== this.Match || this.memo.profile !== this.Profile) {
            this.memo = { match: this.Match, profile: this.Profile, view: this.Build(this.Match, this.Profile) };
        }
        return this.memo.view;
    }

    private Build(match: ResearchRoleMatch, profile: ResearchJobProfile | null): RoleCardView {
        const label = PercentLabel(match.overlap);
        const stats = profile ? RoleCardStats(profile) : EMPTY_STATS;
        const held = HeldSkillNames(match);
        const skills = profile?.topSkills ?? [];
        return {
            Tone: SectorTone(match.jobFamily),
            Percent: Number.parseInt(label, 10),
            PercentLabel: label,
            Salary: stats.salary,
            Education: stats.education,
            Chips: skills.slice(0, MAX_CHIPS).map((s) => ({ Name: s.name, Held: held.has(s.name) })),
            More: Math.max(0, skills.length - MAX_CHIPS),
        };
    }

    /** "Explore role →" (P6:1617); the whole card is the click target too (P6:1613 `data-role`). */
    Explore(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'role', RecordID: this.Match.roleRef });
    }
}
